import {
  Flex,
  Box,
  Image,
  Text,
  HStack,
  Button,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  SkeletonText,
} from "@chakra-ui/react";
import { brandPalette } from "../../../constants/brandPalette";
import {
  adminFooterAvatarBoxProps,
  adminFooterAvatarIconProps,
  adminFooterControlProps,
  adminFooterNameProps,
  adminFooterProps,
  adminFooterSettingsButtonProps,
  adminHeaderSubtitleProps,
  adminNavIconImageProps,
  adminNavItemProps,
  adminSectionLabelProps,
  adminSidebarLogoProps,
  adminSidebarPopoverProps,
  adminSidebarShellProps,
} from "../../../constants/adminSidebarTheme";
import { useAdminTheme } from "../../../context/AdminThemeContext";
import { Link, useLocation } from "react-router-dom";
import React from "react";
import student from "../../../assets/icons/student.svg";
import groups from "../../../assets/icons/groups.svg";
import list from "../../../assets/icons/list.svg";
import videos from "../../../assets/icons/videos.svg";
import events from "../../../assets/icons/events.svg";
import exams from "../../../assets/icons/exams.svg";
import questions from "../../../assets/icons/questions.svg";
import { useDispatch, useSelector } from "react-redux";
import { RiLogoutBoxRLine, RiSettings4Line } from "react-icons/ri";
import { HiSpeakerphone } from "react-icons/hi";
import { FaWhatsapp } from "react-icons/fa";
import { FaUserCircle, FaUserFriends } from "react-icons/fa";
import { logoutUserAction } from "../../../modules/auth/Actions";
import AdminNotificationsBell from "../Notifications/AdminNotificationsBell";
import ThemeToggleButton from "../ThemeToggleButton";

const SuperAdminSidenavbar = ({ embedded = false }) => {
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.user.isLoading);
  const location = useLocation();
  const { themeMode } = useAdminTheme();
  const isDark = themeMode === "dark";
  const footerControls = adminFooterControlProps(isDark);

  const navTabs = [
    { label: "Students", link: "/dashboard/students", icon: student },
    {
      label: "Assistants",
      link: "/super-admin/dashboard/assistants",
      icon: FaUserFriends,
    },
    { label: "Groups", link: "/dashboard/groups", icon: groups },
    { label: "Centers", link: "/dashboard/lectures", icon: events },
    { label: "Content", link: "/dashboard/lessons", icon: list },
    { label: "Student Exams", link: "/dashboard/student-exams", icon: exams },
    {
      label: "Main Student Exams",
      link: "/dashboard/student_exams_main",
      icon: exams,
    },
    { label: "Videos", link: "/dashboard/videos", icon: videos },
    { label: "Views", link: "/super-admin/dashboard/views", icon: videos },
    { label: "Events", link: "/dashboard/events", icon: events },
    { label: "Chapters", link: "/dashboard/chapters", icon: list },
    { label: "Exams", link: "/dashboard/exams", icon: exams },
    {
      label: "Question Bank",
      link: "/dashboard/questions-bank",
      icon: questions,
    },
    {
      label: "Exam Redo Requests",
      link: "/dashboard/exam-redo-requests",
      icon: questions,
    },
    {
      label: "Announcements",
      link: "/dashboard/announcements",
      icon: HiSpeakerphone,
    },
    {
      label: "WhatsApp Templates",
      link: "/dashboard/whatsapp-templates",
      icon: FaWhatsapp,
    },
  ];

  const sections = [
    {
      title: "Super Admin",
      items: navTabs,
    },
  ];

  return (
    <Flex
      as="aside"
      data-admin-sidebar=""
      dir="ltr"
      position={embedded ? "relative" : "fixed"}
      left={embedded ? undefined : "0"}
      top={embedded ? undefined : "0"}
      h={embedded ? "100%" : "100vh"}
      minH={embedded ? "min(100vh, 720px)" : undefined}
      w={embedded ? "100%" : "260px"}
      maxW={embedded ? "100%" : undefined}
      {...adminSidebarShellProps(isDark)}
      flexDirection="column"
      zIndex={embedded ? 1 : "40"}
      overflow="hidden"
    >
      <Box px={6} py={8}>
        <Image
          {...adminSidebarLogoProps(isDark)}
          onClick={() => window.location.replace("/")}
        />
        <Text {...adminHeaderSubtitleProps(isDark)} mt={4} textAlign="center">
          Admin System
        </Text>
      </Box>

      {isLoading ? (
        <HStack w="100%" bg="transparent" h="100%">
          <SkeletonText
            startColor="gray.100"
            endColor="gray.600"
            w="80%"
            mt="4"
            noOfLines={7}
            spacing="20"
          />
        </HStack>
      ) : (
        <Box
          flex="1"
          overflowY="auto"
          px={4}
          pb={8}
          className="scroll"
          sx={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {sections.map((section) => (
            <Box key={section.title} mb={6}>
              <Text {...adminSectionLabelProps(isDark)}>{section.title}</Text>
              <Box>
                {section.items.map((item) => {
                  const active =
                    location.pathname === item.link ||
                    (item.link !== "/" &&
                      location.pathname.startsWith(`${item.link}/`));
                  const isIconAsset = typeof item.icon === "string";
                  const IconCmp = isIconAsset ? null : item.icon;

                  return (
                    <Link key={item.link} to={item.link} style={{ textDecoration: "none" }}>
                      <Flex {...adminNavItemProps(active, isDark)}>
                        {isIconAsset ? (
                          <Image src={item.icon} {...adminNavIconImageProps(active, isDark)} />
                        ) : (
                          <Box as={IconCmp} fontSize="18px" />
                        )}
                        <Text flex="1" textAlign="left">
                          {item.label}
                        </Text>
                      </Flex>
                    </Link>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Flex {...adminFooterProps(isDark)} align="center" gap={3} justify="space-between">
        <Flex align="center" gap={3} minW={0} flex="1">
          <Flex {...adminFooterAvatarBoxProps(isDark)}>
            <Box as={FaUserCircle} {...adminFooterAvatarIconProps(isDark)} />
          </Flex>
          <Box overflow="hidden">
            <Text {...adminFooterNameProps(isDark)}>Admin Profile</Text>
          </Box>
        </Flex>

        <AdminNotificationsBell {...footerControls.bell} />
        <ThemeToggleButton {...footerControls.themeToggle} />
        <Popover placement="top-end" closeOnBlur>
          <PopoverTrigger>
            <IconButton
              aria-label="Account settings"
              icon={<RiSettings4Line size={22} />}
              {...adminFooterSettingsButtonProps(isDark)}
            />
          </PopoverTrigger>
          <PopoverContent
            w="220px"
            border="1px solid"
            {...adminSidebarPopoverProps(isDark)}
            bg="var(--surface-raised)"
            boxShadow="lg"
            _focus={{ outline: "none" }}
          >
            <PopoverArrow
              bg="var(--surface-raised)"
              borderColor={adminSidebarPopoverProps(isDark).borderColor}
            />
            <PopoverBody p={3}>
              <Button
                w="100%"
                justifyContent="flex-start"
                leftIcon={<RiLogoutBoxRLine size={20} />}
                bg={brandPalette.primary}
                color="white"
                size="sm"
                fontWeight="700"
                borderRadius="lg"
                _hover={{ bg: brandPalette.secondary }}
                onClick={() => dispatch(logoutUserAction())}
              >
                Log out
              </Button>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </Flex>
    </Flex>
  );
};

export default SuperAdminSidenavbar;
